/* Goldschatz Honig – Zurück-nach-oben-Button
   Custom Element <back-to-top>. Kein jQuery, keine Abhängigkeiten.

   Positionierung: Der Button misst den tatsächlichen Inhaltsblock, der sich auf
   seiner Höhe im Viewport befindet, und hängt sich außen daran. Dadurch sitzt er
   auf schmalen Textseiten näher am Text als auf breiten Shop-Grids.
   Fällt auf --page-width zurück, wenn kein Block gefunden wird. */

if (!customElements.get('back-to-top')) {
  customElements.define(
    'back-to-top',
    class BackToTop extends HTMLElement {
      constructor() {
        super();
        this.ticking = false;
        this.lastX = null;
        this.onScroll = this.onScroll.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector('.back-to-top__button');
        if (!this.button) return;

        this.threshold = parseInt(this.dataset.threshold, 10) || 600;
        this.autoScheme = this.dataset.autoScheme === 'true';
        this.baseScheme = this.dataset.baseScheme || '';
        this.isLeft = this.classList.contains('back-to-top--left');

        this.button.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        this.update();
        /* Nach dem Laden der Bilder verschieben sich Blöcke oft noch einmal. */
        window.addEventListener('load', () => this.update(), { once: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      update() {
        const visible = window.scrollY > this.threshold;
        this.classList.toggle('is-visible', visible);
        this.measure();
        if (visible && this.autoScheme) this.syncScheme();
      }

      /* Abstand zum Inhaltsblock aus dem Theme-Setting, umgerechnet in Pixel. */
      get gap() {
        const rem = parseFloat(getComputedStyle(this).getPropertyValue('--btt-inset')) || 0;
        const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
        return rem * root;
      }

      /* Sucht den Inhaltsblock auf Höhe des Buttons und richtet daran aus. */
      measure() {
        const vw = document.documentElement.clientWidth;
        const size = this.offsetWidth || 48;
        const edge = vw < 750 ? 16 : 20;
        const rect = this.getBoundingClientRect();

        /* Sondierungspunkt: horizontal Bildschirmmitte, vertikal Button-Mitte. */
        const probeY = Math.min(Math.max(rect.top + rect.height / 2, 8), window.innerHeight - 8);

        let node = document.elementFromPoint(vw / 2, probeY);
        let block = null;

        while (node && node !== document.body && node !== document.documentElement) {
          const w = node.getBoundingClientRect().width;
          /* Breit genug um ein Container zu sein, schmal genug um nicht
             die volle Fensterbreite zu sein. */
          if (w >= 240 && w <= vw * 0.94) {
            block = node;
            break;
          }
          node = node.parentElement;
        }

        let x;

        if (block) {
          const b = block.getBoundingClientRect();
          x = this.isLeft ? b.left - this.gap - size : b.right + this.gap;
        } else {
          /* Fallback: Seitenspalte aus --page-width. */
          const pw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--page-width')) || 120;
          const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
          const col = Math.min(pw * rootPx, vw);
          x = this.isLeft ? (vw - col) / 2 - this.gap - size : (vw + col) / 2 + this.gap;
        }

        /* Nie aus dem Fenster laufen, nie in die andere Bildschirmhälfte wandern. */
        x = Math.min(x, vw - size - edge);
        x = Math.max(x, edge);

        /* Zittern beim Scrollen vermeiden. */
        if (this.lastX !== null && Math.abs(x - this.lastX) < 2) return;
        this.lastX = x;

        this.style.setProperty('--btt-x', `${Math.round(x)}px`);
        this.classList.add('back-to-top--measured');
      }

      /* Übernimmt das Farbschema des Abschnitts, über dem der Button gerade liegt. */
      syncScheme() {
        if (typeof document.elementsFromPoint !== 'function') return;

        const rect = this.getBoundingClientRect();
        const match = document
          .elementsFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
          .find(
            (node) =>
              node !== this &&
              !this.contains(node) &&
              Array.from(node.classList).some((cls) => /^color-scheme-/.test(cls))
          );

        const scheme = match
          ? Array.from(match.classList).find((cls) => /^color-scheme-/.test(cls))
          : this.baseScheme;

        if (!scheme || this.classList.contains(scheme)) return;

        Array.from(this.classList)
          .filter((cls) => /^color-scheme-/.test(cls))
          .forEach((cls) => this.classList.remove(cls));

        this.classList.add(scheme);
      }

      onClick() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

        /* Fokus an den Seitenanfang zurückgeben. Bewusst auf <body>:
           der Skip-Link würde sich beim Fokussieren sichtbar einblenden. */
        const body = document.body;
        if (!body.hasAttribute('tabindex')) {
          body.setAttribute('tabindex', '-1');
        }
        body.focus({ preventScroll: true });
        body.addEventListener('blur', () => body.removeAttribute('tabindex'), { once: true });
      }
    }
  );
}